/**
 * Barra lateral de navegación.
 *
 * Renderiza las secciones visibles para la sesión (`useNavSections`, que replica
 * el orden y el gating del SSR). Cada entrada es un `NavLink` con su icono Font
 * Awesome (los mismos glifos que `templates/base.html`). Presentacional: no
 * decide permisos (eso vive en el modelo de navegación); aquí solo se pinta.
 *
 * Estética del proyecto (paridad con el SSR): superficie azul clara, cabecera
 * con el logo, títulos de sección en azul y el ítem activo con fondo azul tenue
 * y texto azul oscuro. Redondeado (`rounded-lg`).
 */
import { NavLink } from "react-router-dom";

import { cn } from "@/components/ui";

import { useNavSections } from "../useNavSections";

export interface SidebarProps {
  /**
   * Se invoca al activar cualquier entrada. El layout lo usa para cerrar el
   * drawer en móvil/tableta tras navegar (en escritorio no se cierra).
   *
   * Incluye `undefined` explícito por `exactOptionalPropertyTypes`: el layout
   * pasa `undefined` en escritorio para no cerrar al navegar.
   */
  onNavigate?: (() => void) | undefined;
}

export function Sidebar({ onNavigate }: SidebarProps) {
  const sections = useNavSections();

  return (
    <nav
      id="navegacion-principal"
      aria-label="Navegación principal"
      className="flex h-full flex-col overflow-y-auto bg-[#d8e9f7] text-sm"
    >
      <div className="flex h-14 shrink-0 items-center gap-2.5 border-b border-[#9ec5e8] bg-[linear-gradient(180deg,rgba(37,99,235,0.12)_0%,rgba(255,255,255,0.35)_100%)] px-4">
        <img
          src="/LogoL.jpeg"
          alt=""
          aria-hidden="true"
          className="h-9 w-9 shrink-0 rounded-md object-cover shadow-sm ring-2 ring-white/80"
        />
        <div className="min-w-0 leading-tight">
          <div className="truncate text-sm font-semibold text-[#0c3560]">
            Láminas &amp; Dobleces
          </div>
          <div className="truncate text-xs font-medium text-[#2563eb]">Gestión</div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-2 py-3">
        {sections.map((section) => (
          <div key={section.id} className="mb-1">
            {section.title !== "" && (
              <div className="px-3 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-[#2b5f9e]">
                {section.title}
              </div>
            )}
            {section.items.map((item) => (
              <NavLink
                key={item.id}
                to={item.to}
                // `end` en la raíz para que "Inicio" no quede activo en subrutas.
                end={item.to === "/"}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    "mb-1 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-150 ease-out",
                    isActive
                      ? "bg-[#bfe0f8] font-semibold text-[#1e3a8a]"
                      : "text-[#143a63] hover:bg-[#bfe0f8] hover:text-[#0c4a8c]",
                  )
                }
              >
                {item.icon && (
                  <i
                    className={cn(
                      item.icon,
                      "inline-flex h-5 w-5 shrink-0 items-center justify-center text-[13px] text-[#1d4ed8]",
                    )}
                    aria-hidden="true"
                  />
                )}
                {item.label}
              </NavLink>
            ))}
          </div>
        ))}
      </div>
    </nav>
  );
}
